// NirmalX explorer: chain facts and copy for the custom home page.

/**
 * The testnet's QBFT validators, named Alpha, Beta and Gamma on the explorer (the user's choice, 2026-10-06).
 * Servers: Alpha = tn-val-01, Beta = tn-val-02, Gamma = tn-val-03. Mainnet will need its own entries.
 */
export const VALIDATORS: ReadonlyArray<{ hash: string; name: string; tag: string }> = [
  { hash: '0x5eb706061c0be34577e56195e7bc2e89dec58709', name: 'Alpha', tag: 'α' },
  { hash: '0xbfc12dc68f02763c04b49142ba06c466e2aebe96', name: 'Beta', tag: 'β' },
  { hash: '0x251d8da5c0a990728e5bf3f379c491b80ba887ab', name: 'Gamma', tag: 'γ' },
];

export function findValidator(hash: string | undefined) {
  if (!hash) {
    return undefined;
  }
  return VALIDATORS.find((validator) => validator.hash === hash.toLowerCase());
}

/** The testnet's genesis allocations (1,000,000,000 NRXN each), shown as "Genesis allocation" on their address pages. */
export const GENESIS_ALLOCATIONS: ReadonlyArray<string> = [
  '0x201bdf209eaf64288e5586b709b15ec9799f4fb8',
  '0x55c5dfcc2f827f0527f37c7b2c508d7eab6b36fe',
];

/** Block timing from the genesis: 2 s while there are transactions, one empty block every 60 s when idle. */
export const BLOCK_PERIOD_S = 2;
export const EMPTY_BLOCK_PERIOD_S = 60;

/**
 * The hype banner's countdown target. Set by the user on 2026-10-06 to one week out: Tue 13 Oct 2026, 12:00 PKT.
 * It counts down to a "big reveal", not to a mainnet launch date (mainnet is still in the pipeline).
 */
export const REVEAL_AT = '2026-10-13T12:00:00+05:00';

/** The NirmalX website, linked from the explorer's header and footer. Change it when the domain is live. */
export const WEBSITE_URL = 'https://nirmalx.io';

/** The scrolling announcement strip. Future tense and no dates: nothing here is launched yet. */
export const TICKER = [
  'NirmalX Mainnet is in the pipeline',
  'Rise Beyond Limits',
  'Something bigger is rising',
  'Building Today. Empowering Tomorrow.',
];

export type HorizonItem = { title: string; status: string; text: string };

/** "On the horizon": the milestones still to come (MILESTONES.md 2, 4 and 5). */
export const HORIZON: ReadonlyArray<HorizonItem> = [
  {
    title: 'NirmalX Mainnet',
    status: 'In the pipeline',
    text: 'The production chain is being prepared, carrying forward everything proven on this testnet.',
  },
  {
    title: 'The full NirmalX Explorer',
    status: 'In progress',
    text: 'Every screen is being rebuilt in this style: blocks, transactions, addresses and tokens.',
  },
  {
    title: 'NRXN Staking',
    status: 'Planned',
    text: 'A staking dApp for NRXN holders is on the roadmap, built on the same chain.',
  },
];

export function shortHash(hash: string, head = 6, tail = 4) {
  return hash.length <= head + tail + 1 ? hash : `${ hash.slice(0, head) }…${ hash.slice(-tail) }`;
}

/** "12s ago", "3m ago", "2h ago" from a timestamp and the current time (ms). */
export function ago(timestamp: string | null | undefined, now: number) {
  if (!timestamp) {
    return '';
  }
  const seconds = Math.max(0, Math.round((now - new Date(timestamp).getTime()) / 1000));
  if (seconds < 60) {
    return `${ seconds }s ago`;
  }
  if (seconds < 3600) {
    return `${ Math.floor(seconds / 60) }m ago`;
  }
  return `${ Math.floor(seconds / 3600) }h ago`;
}
